// Power Skills Wheel - Category-based structure with 12 categories, 3 skills each

// Category to skills mapping
export const POWER_SKILLS = {
  "Listen": [
    "Active Listening",
    "Feedback",
    "Presence"
  ],
  "Express": [
    "Clear Communication",
    "Storytelling",
    "Persuasion"
  ],
  "Self-Command": [
    "Emotional Self-Control",
    "Calm Confidence",
    "Self-Awareness"
  ],
  "Focus": [
    "Focus",
    "Timeboxing",
    "Prioritization"
  ],
  "Decide": [
    "Decision-Making",
    "Problem Framing",
    "Strategic Patience"
  ],
  "See": [
    "Systems Thinking",
    "Opportunity Spotting",
    "Curiosity"
  ],
  "Collaborate": [
    "Collaboration",
    "Relationship Building",
    "Influence"
  ],
  "Navigate": [
    "Conflict Navigation",
    "Negotiation",
    "Self-Advocacy"
  ],
  "Own It": [
    "Accountability",
    "Follow-Through",
    "Reliability"
  ],
  "Adapt": [
    "Adaptability",
    "Learning Agility",
    "Resourcefulness"
  ],
  "Create": [
    "Creativity",
    "Initiative",
    "Simplify Under Pressure"
  ],
  "Steady": [
    "Boundary Setting",
    "Attention to Detail",
    "Generosity"
  ]
};

// Category order for the wheel (clockwise from top)
export const CATEGORIES = [
  "Listen",
  "Express", 
  "Self-Command",
  "Focus",
  "Decide",
  "See",
  "Collaborate",
  "Navigate",
  "Own It",
  "Adapt",
  "Create",
  "Steady"
];

// Full skill details (keeping existing data structure for skill cards)
export const SKILL_DETAILS = {
  "Active Listening": {
    id: "active_listening",
    definition: "Hearing what someone actually says before planning your reply.",
    reps: {
      job_search: "Read the job post again and note what you missed.",
      networking: "Ask a follow-up question based on their last sentence.",
      interview: "Pause after the interviewer finishes before you respond.",
      business: "Summarize what a customer said before offering a solution.",
      daily_life: "Repeat back what someone told you before adding your view."
    },
    easier_action: "Notice one thing someone said that you almost missed.",
    compass_lens: "north",
    compass_question: "What do they need from this conversation?",
    fivec: "consent",
    fivec_line: "Listening shows you value their voice.",
    voice_line: "Active Listening - When you listen, you notice things others might miss."
  },
  "Feedback": {
    id: "feedback",
    definition: "Giving and receiving input that helps people improve.",
    reps: {
      job_search: "Ask one person why you did not get the role.",
      networking: "Thank someone for their advice and share what you did with it.",
      interview: "Request feedback after a rejection.",
      business: "Ask a customer what one thing you could do better.",
      daily_life: "Tell someone one thing they did well today."
    },
    easier_action: "Think of one piece of feedback you have been avoiding.",
    compass_lens: "north",
    compass_question: "What does this person need to hear?",
    fivec: "care",
    fivec_line: "Honest input is a gift.",
    voice_line: "Feedback - One honest suggestion is more valuable than many compliments, as long as you use it."
  },
  "Presence": {
    id: "presence",
    definition: "Being fully in the moment with the people in front of you.",
    reps: {
      job_search: "Close your laptop during a networking call.",
      networking: "Put your phone away during a conversation.",
      interview: "Make eye contact and stay still while the interviewer talks.",
      business: "Give a customer your complete attention for five minutes.",
      daily_life: "Have one conversation without thinking about the next task."
    },
    easier_action: "Notice where your attention is right now.",
    compass_lens: "east",
    compass_question: "What is pulling you away from this moment?",
    fivec: "care",
    fivec_line: "Attention is the rarest gift you can give.",
    voice_line: "Presence - Really being present is rare and more valuable than most people think."
  },
  "Clear Communication": {
    id: "clear_communication",
    definition: "Saying what you mean in a way others understand the first time.",
    reps: {
      job_search: "Rewrite one bullet point to cut the jargon.",
      networking: "Send a message that gets to the point in two sentences.",
      interview: "Answer one question without filler words.",
      business: "Simplify one pitch until a stranger would get it.",
      daily_life: "Make one request using fewer than ten words."
    },
    easier_action: "Rephrase one thing you said today more directly.",
    compass_lens: "south",
    compass_question: "What structure would make this message land?",
    fivec: "care",
    fivec_line: "Being understood is an act of respect.",
    voice_line: "Clear Communication - Say what you mean right away so everyone is on the same page."
  },
  "Storytelling": {
    id: "storytelling",
    definition: "Turning your experience into a narrative that makes others care.",
    reps: {
      job_search: "Rewrite one resume bullet as a before-and-after story.",
      networking: "Share a quick story instead of listing your role.",
      interview: "Answer a question with a 60-second story that has a clear ending.",
      business: "Turn one customer result into a story you can tell in two minutes.",
      daily_life: "Share something that happened today as a story with a point."
    },
    easier_action: "Think of one thing you did recently and find the story in it.",
    compass_lens: "north",
    compass_question: "What does this audience need to feel?",
    fivec: "culture",
    fivec_line: "Stories carry meaning further than facts.",
    voice_line: "Storytelling - People remember the way you made them feel, even if they forget your words."
  },
  "Persuasion": {
    id: "persuasion",
    definition: "Helping others see why your idea serves their interests too.",
    reps: {
      job_search: "Reframe your pitch to show what the employer gains.",
      networking: "Make your ask easy to say yes to.",
      interview: "Show how your strengths solve their specific problem.",
      business: "Remove one objection before they raise it.",
      daily_life: "Present an idea in a way that makes the other person feel smart for agreeing."
    },
    easier_action: "Think of one way to make your next request easier to accept.",
    compass_lens: "north",
    compass_question: "What does this person need to feel to say yes?",
    fivec: "consent",
    fivec_line: "Good persuasion respects the other person's agency.",
    voice_line: "Persuasion - People agree when they know exactly what they're saying yes to. Be clear."
  },
  "Emotional Self-Control": {
    id: "emotional_self_control",
    definition: "Choosing your response instead of reacting automatically.",
    reps: {
      job_search: "Pause before responding to a frustrating rejection email.",
      networking: "Wait before replying to a message that annoyed you.",
      interview: "Take a breath before answering a question that triggers you.",
      business: "Sleep on a decision that feels urgent but isn't.",
      daily_life: "Count to five before responding to something that upset you."
    },
    easier_action: "Notice one moment today where you reacted before you thought.",
    compass_lens: "east",
    compass_question: "What emotion is driving this reaction?",
    fivec: "capacity",
    fivec_line: "The pause is where your power lives.",
    voice_line: "Emotional Self-Control - The moment between what happens and how you react is the most important one."
  },
  "Calm Confidence": {
    id: "calm_confidence",
    definition: "Trusting yourself without needing to prove it to others.",
    reps: {
      job_search: "Submit an application without rereading it ten times.",
      networking: "Introduce yourself without apologizing for reaching out.",
      interview: "Sit back in your chair and speak at a slower pace.",
      business: "State your rate without justifying it.",
      daily_life: "Make a decision and move on without second-guessing."
    },
    easier_action: "Take one deep breath before your next action.",
    compass_lens: "east",
    compass_question: "What emotion is shaking your confidence?",
    fivec: "care",
    fivec_line: "You do not need to convince everyone.",
    voice_line: "Calm Confidence - You don't have to know everything. What counts is that you show up."
  },
  "Self-Awareness": {
    id: "self_awareness",
    definition: "Recognizing your patterns, triggers, and tendencies as they happen.",
    reps: {
      job_search: "Notice when you're avoiding applications and name why.",
      networking: "Recognize when you're holding back and identify the fear.",
      interview: "Catch yourself rambling and course-correct.",
      business: "Notice when you're procrastinating on something important.",
      daily_life: "Observe one habit you repeat without thinking."
    },
    easier_action: "Notice one thing you did today on autopilot.",
    compass_lens: "east",
    compass_question: "What pattern keeps showing up here?",
    fivec: "capacity",
    fivec_line: "You can only change what you notice.",
    voice_line: "Self-Awareness - Habits take time to build and change. The first step is to notice them."
  },
  "Focus": {
    id: "focus",
    definition: "Giving one thing your full attention until it is done.",
    reps: {
      job_search: "Close all tabs except the one you need for the next task.",
      networking: "Write one message without checking your phone.",
      interview: "Practice one story without interruption.",
      business: "Work on one feature for 45 minutes with no distractions.",
      daily_life: "Do one chore from start to finish before switching."
    },
    easier_action: "Remove one distraction from your workspace right now.",
    compass_lens: "east",
    compass_question: "What pulls your attention away?",
    fivec: "capacity",
    fivec_line: "Depth beats breadth here.",
    voice_line: "Focus - Most distractions aren't as important as what you're working on right now."
  },
  "Timeboxing": {
    id: "timeboxing",
    definition: "Limiting how long you spend on a task before moving on.",
    reps: {
      job_search: "Apply to five roles in 90 minutes, then stop.",
      networking: "Draft three messages in 20 minutes, not 60.",
      interview: "Practice one answer for exactly five minutes.",
      business: "Give yourself 30 minutes to finish a draft, not all day.",
      daily_life: "Set a timer for one task and stop when it rings."
    },
    easier_action: "Set a 15-minute timer for something you keep putting off.",
    compass_lens: "south",
    compass_question: "What container would make this manageable?",
    fivec: "capacity",
    fivec_line: "Limits create momentum.",
    voice_line: "Timeboxing - If you give a task unlimited time, it will take unlimited time."
  },
  "Prioritization": {
    id: "prioritization",
    definition: "Choosing what matters most before starting anything else.",
    reps: {
      job_search: "Pick your top three target companies and ignore the rest today.",
      networking: "Reach out to the most relevant contact first.",
      interview: "Prepare for the question you fear most before the easy ones.",
      business: "Work on the task with the highest return before busy work.",
      daily_life: "Do the hardest thing on your list in the first hour."
    },
    easier_action: "Write down the one thing that would move the needle most.",
    compass_lens: "south",
    compass_question: "What system would help you rank these?",
    fivec: "capacity",
    fivec_line: "Choosing means some things wait.",
    voice_line: "Prioritization - Focusing on one thing at a time gets you farther than juggling everything."
  },
  "Decision-Making": {
    id: "decision_making",
    definition: "Choosing a direction and acting before you have all the answers.",
    reps: {
      job_search: "Pick one offer to pursue instead of keeping all options open.",
      networking: "Decide which event to attend without overthinking.",
      interview: "Give a definitive answer to a hypothetical question.",
      business: "Ship a version instead of debating features longer.",
      daily_life: "Make one choice in under a minute today."
    },
    easier_action: "Make one small decision you have been putting off.",
    compass_lens: "south",
    compass_question: "What framework would help you choose?",
    fivec: "consequence",
    fivec_line: "A good decision now beats a perfect one later.",
    voice_line: "Decision-Making - Making a good decision now is better than waiting forever for the perfect one."
  },
  "Problem Framing": {
    id: "problem_framing",
    definition: "Defining the real problem before jumping to solutions.",
    reps: {
      job_search: "Write down the actual obstacle in your search.",
      networking: "Ask what problem someone is really trying to solve.",
      interview: "Clarify the question before you start answering.",
      business: "Rewrite a problem statement until it fits in one sentence.",
      daily_life: "State the issue in simple terms before fixing it."
    },
    easier_action: "Describe one problem you are facing in one sentence.",
    compass_lens: "south",
    compass_question: "What system underlies this problem?",
    fivec: "capacity",
    fivec_line: "The right question is half the answer.",
    voice_line: "Problem Framing - The questions you start with shape your solutions, so choose them carefully."
  },
  "Strategic Patience": {
    id: "strategic_patience",
    definition: "Waiting on purpose when action would not help.",
    reps: {
      job_search: "Wait 48 hours before following up on an application.",
      networking: "Let a relationship develop before asking for anything.",
      interview: "Pause before answering to give yourself time to think.",
      business: "Hold off on a launch until the timing improves.",
      daily_life: "Wait one day before reacting to something that upset you."
    },
    easier_action: "Identify one place where rushing has hurt you.",
    compass_lens: "east",
    compass_question: "What emotion is pushing you to move too fast?",
    fivec: "consequence",
    fivec_line: "Good timing changes outcomes.",
    voice_line: "Strategic Patience - Sometimes, waiting for more information is the smartest move."
  },
  "Systems Thinking": {
    id: "systems_thinking",
    definition: "Seeing how parts connect and affect each other over time.",
    reps: {
      job_search: "Map how one networking action leads to multiple outcomes.",
      networking: "Notice which relationship creates the most introductions.",
      interview: "Explain how your role affected other teams.",
      business: "Draw how your funnel moves a customer from start to finish.",
      daily_life: "Trace how one habit affects three other parts of your day."
    },
    easier_action: "Identify one cause-and-effect chain in your current work.",
    compass_lens: "south",
    compass_question: "What patterns repeat here?",
    fivec: "capacity",
    fivec_line: "Small changes ripple outward.",
    voice_line: "Systems Thinking - Before you try to fix a problem, notice who benefits from things staying the same."
  },
  "Opportunity Spotting": {
    id: "opportunity_spotting",
    definition: "Noticing openings others walk past.",
    reps: {
      job_search: "Find one hidden job through a news article or company update.",
      networking: "Identify a way to help two people you know.",
      interview: "Spot a chance to mention a relevant skill unprompted.",
      business: "Notice a gap in your market this week.",
      daily_life: "See one chance to improve something you do every day."
    },
    easier_action: "Look at one familiar situation and ask what is missing.",
    compass_lens: "west",
    compass_question: "What possibility have you not considered?",
    fivec: "culture",
    fivec_line: "Good things hide in plain sight.",
    voice_line: "Opportunity Spotting - The best opportunities often look like problems that no one wants to deal with at first."
  },
  "Curiosity": {
    id: "curiosity",
    definition: "Asking questions that lead somewhere you did not expect.",
    reps: {
      job_search: "Research something about a company that surprised you.",
      networking: "Ask someone about their least obvious skill.",
      interview: "Ask the interviewer what they wish candidates knew.",
      business: "Interview a customer about something outside your product.",
      daily_life: "Learn one fact about a topic you know nothing about."
    },
    easier_action: "Ask one why question about something you take for granted.",
    compass_lens: "north",
    compass_question: "What do you not yet understand about their situation?",
    fivec: "care",
    fivec_line: "Curiosity shows respect.",
    voice_line: "Curiosity - The question no one has asked yet is often the one that matters most."
  },
  "Collaboration": {
    id: "collaboration",
    definition: "Working with others in a way that makes the result better.",
    reps: {
      job_search: "Partner with someone on a shared job lead.",
      networking: "Introduce yourself as part of a team, not just solo.",
      interview: "Share credit in a story where you led something.",
      business: "Co-create a deliverable instead of working alone.",
      daily_life: "Ask someone to help with a task you usually do solo."
    },
    easier_action: "Identify one task where a second brain would help.",
    compass_lens: "north",
    compass_question: "What would this look like as a team effort?",
    fivec: "culture",
    fivec_line: "Better together is usually true.",
    voice_line: "Collaboration - People who help others improve are always in demand."
  },
  "Relationship Building": {
    id: "relationship_building",
    definition: "Creating connections that grow stronger over time.",
    reps: {
      job_search: "Reconnect with someone you lost touch with.",
      networking: "Send a note to someone with no ask attached.",
      interview: "Thank the interviewer with a specific detail from your talk.",
      business: "Check in with a past client just to see how they are.",
      daily_life: "Reach out to one person you have been meaning to contact."
    },
    easier_action: "Think of one person you could message right now.",
    compass_lens: "north",
    compass_question: "What does this relationship need to grow?",
    fivec: "culture",
    fivec_line: "Relationships are built in small moments.",
    voice_line: "Relationship Building - Trust grows over time through small, steady actions."
  },
  "Influence": {
    id: "influence",
    definition: "Shaping how others think or act through credibility and trust.",
    reps: {
      job_search: "Share one insight online that positions you as helpful.",
      networking: "Offer a perspective that changes how someone sees a problem.",
      interview: "Lead the conversation toward your strengths.",
      business: "Write one piece of content that shifts perception.",
      daily_life: "Suggest an idea in a way that others want to adopt."
    },
    easier_action: "Share one helpful thought without expecting anything back.",
    compass_lens: "north",
    compass_question: "What does this audience need to hear?",
    fivec: "consent",
    fivec_line: "Influence is earned, not demanded.",
    voice_line: "Influence - People follow those who make them feel their work is important."
  },
  "Conflict Navigation": {
    id: "conflict_navigation",
    definition: "Moving through disagreements without escalating or avoiding.",
    reps: {
      job_search: "Address a gap in your resume honestly if asked.",
      networking: "Disagree politely in a conversation without backing down.",
      interview: "Answer a tough question about past friction professionally.",
      business: "Have a direct conversation about a scope issue.",
      daily_life: "Bring up one small issue before it becomes big."
    },
    easier_action: "Name one tension you have been avoiding.",
    compass_lens: "north",
    compass_question: "What does the other person need here?",
    fivec: "consequence",
    fivec_line: "Handled early, conflict stays small.",
    voice_line: "Conflict Navigation - If you avoid tension now, it often comes back even stronger later."
  },
  "Negotiation": {
    id: "negotiation",
    definition: "Finding terms that work for both sides without giving up what matters.",
    reps: {
      job_search: "Ask for one thing in your offer that was not mentioned.",
      networking: "Propose a mutual exchange instead of just asking for help.",
      interview: "Counter a lowball with a specific number and reason.",
      business: "Adjust scope instead of just lowering price.",
      daily_life: "Suggest a trade when someone asks for a favor."
    },
    easier_action: "Identify one thing you could ask for but have not.",
    compass_lens: "west",
    compass_question: "What if you asked for more?",
    fivec: "consent",
    fivec_line: "Both sides should leave satisfied.",
    voice_line: "Negotiation - You won't get what you don't ask for."
  },
  "Self-Advocacy": {
    id: "self_advocacy",
    definition: "Asking for what you need without waiting to be offered.",
    reps: {
      job_search: "Ask a recruiter to expedite your process.",
      networking: "Tell someone exactly what kind of intro you need.",
      interview: "State your salary range clearly.",
      business: "Ask for a testimonial from a satisfied client.",
      daily_life: "Request help from someone who can give it."
    },
    easier_action: "Identify one thing you need that you have not asked for.",
    compass_lens: "west",
    compass_question: "What if you asked directly?",
    fivec: "consent",
    fivec_line: "You have the right to ask.",
    voice_line: "Self-Advocacy - People who speak up about their needs are more likely to have them met."
  },
  "Accountability": {
    id: "accountability",
    definition: "Owning what you said you would do and reporting on it.",
    reps: {
      job_search: "Tell someone your application target for the week.",
      networking: "Follow up on a promise you made in a past conversation.",
      interview: "Share a specific example of owning a past mistake.",
      business: "Send a status update before anyone asks.",
      daily_life: "Check off one task you committed to yesterday."
    },
    easier_action: "Name one commitment you have not followed through on.",
    compass_lens: "south",
    compass_question: "What system would keep you on track?",
    fivec: "consequence",
    fivec_line: "Keeping your word compounds over time.",
    voice_line: "Accountability - Admitting mistakes early helps you move forward faster."
  },
  "Follow-Through": {
    id: "follow_through",
    definition: "Finishing what you started, especially the last 10 percent.",
    reps: {
      job_search: "Complete one unfinished application today.",
      networking: "Send the follow-up you said you would send.",
      interview: "Write the thank-you note within 24 hours.",
      business: "Ship the feature that has been 90 percent done.",
      daily_life: "Finish one thing that has been lingering on your list."
    },
    easier_action: "Identify one thing that is almost done and close it out.",
    compass_lens: "south",
    compass_question: "What is stopping you from finishing this?",
    fivec: "consequence",
    fivec_line: "Almost done is not done.",
    voice_line: "Follow-Through - Finishing small tasks on time helps you earn bigger opportunities."
  },
  "Reliability": {
    id: "reliability",
    definition: "Doing what you said when you said you would do it.",
    reps: {
      job_search: "Send your follow-up email exactly when you promised.",
      networking: "Show up on time to a call you scheduled.",
      interview: "Arrive five minutes early, not just on time.",
      business: "Deliver something small ahead of the deadline.",
      daily_life: "Complete one promise you made to yourself this week."
    },
    easier_action: "Pick one thing you said you would do and finish it now.",
    compass_lens: "south",
    compass_question: "What would make this repeatable?",
    fivec: "culture",
    fivec_line: "People remember who shows up.",
    voice_line: "Reliability - Keeping your promises is one of the simplest ways to build trust."
  },
  "Adaptability": {
    id: "adaptability",
    definition: "Adjusting your approach when conditions change without losing momentum.",
    reps: {
      job_search: "Update one line in your resume and apply again.",
      networking: "Follow up with a different angle than your first message.",
      interview: "Ask for a moment to think, then pivot your answer.",
      business: "Test a new hook before changing the whole offer.",
      daily_life: "Pick the next best plan in under 2 minutes."
    },
    easier_action: "Identify one small adjustment you can make right now.",
    compass_lens: "east",
    compass_question: "What feeling is making this change hard?",
    fivec: "capacity",
    fivec_line: "You have enough range to handle this.",
    voice_line: "Adaptability - You've handled challenges before, so you can handle this one as well."
  },
  "Learning Agility": {
    id: "learning_agility",
    definition: "Picking up new skills quickly by applying what you already know.",
    reps: {
      job_search: "Learn one new platform and apply to three jobs there.",
      networking: "Try a new outreach method you read about.",
      interview: "Research a topic that came up unexpectedly and share what you found.",
      business: "Use a new tool to solve a problem this week.",
      daily_life: "Learn one small thing and use it the same day."
    },
    easier_action: "Watch one short tutorial on something you need.",
    compass_lens: "south",
    compass_question: "What pattern from past learning applies here?",
    fivec: "capacity",
    fivec_line: "You have learned hard things before.",
    voice_line: "Learning Agility - You don't need to know everything. Just be ready to learn quickly."
  },
  "Resourcefulness": {
    id: "resourcefulness",
    definition: "Solving problems with what you have instead of what you wish you had.",
    reps: {
      job_search: "Find a back door to a company using existing connections.",
      networking: "Offer value using a skill you already have.",
      interview: "Turn a gap into a story about learning fast.",
      business: "Launch with a free tool instead of waiting for budget.",
      daily_life: "Fix something using items already in your home."
    },
    easier_action: "List three resources you have that you are not using.",
    compass_lens: "west",
    compass_question: "What if you worked with what you have?",
    fivec: "capacity",
    fivec_line: "Constraints sharpen creativity.",
    voice_line: "Resourcefulness - You have more resources than you realize."
  },
  "Creativity": {
    id: "creativity",
    definition: "Combining ideas in new ways to produce something useful.",
    reps: {
      job_search: "Try an application format you have never used before.",
      networking: "Suggest an unexpected way to connect with someone.",
      interview: "Answer a standard question with a non-standard example.",
      business: "Combine two features into one that solves a new problem.",
      daily_life: "Do a routine task in a completely different way."
    },
    easier_action: "Think of one alternative approach to your current task.",
    compass_lens: "west",
    compass_question: "What would this look like done differently?",
    fivec: "culture",
    fivec_line: "New ideas need room to breathe.",
    voice_line: "Creativity - You don't need a full plan. Try out one small idea this week."
  },
  "Initiative": {
    id: "initiative",
    definition: "Starting something useful before being told to.",
    reps: {
      job_search: "Reach out to a company before they post a role.",
      networking: "Introduce two people who should know each other.",
      interview: "Bring a one-page idea to your next interview.",
      business: "Launch a small experiment without waiting for approval.",
      daily_life: "Start one project you have been putting off."
    },
    easier_action: "Do one small thing without waiting for instructions.",
    compass_lens: "west",
    compass_question: "What could you try without asking?",
    fivec: "capacity",
    fivec_line: "Starting is its own reward.",
    voice_line: "Initiative - The first step belongs to whoever takes it."
  },
  "Simplify Under Pressure": {
    id: "simplify_under_pressure",
    definition: "Reducing complexity to one clear action when everything feels urgent.",
    reps: {
      job_search: "When overwhelmed, identify just the next application to send.",
      networking: "When stressed about outreach, pick one person to message.",
      interview: "When anxious, focus only on the next question.",
      business: "When swamped, identify the one thing that moves the needle.",
      daily_life: "When everything feels urgent, write down just one next step."
    },
    easier_action: "Name one next step you can take in the next five minutes.",
    compass_lens: "south",
    compass_question: "What is the smallest useful action here?",
    fivec: "capacity",
    fivec_line: "One step is still forward.",
    voice_line: "Simplify Under Pressure - When everything feels urgent, the most useful thing you can do is find the one next step."
  },
  "Boundary Setting": {
    id: "boundary_setting",
    definition: "Knowing where your limits are and communicating them calmly.",
    reps: {
      job_search: "Decline one request that drains energy from your search.",
      networking: "Say no to a coffee chat that does not fit your goals.",
      interview: "Ask about expectations before agreeing to extra steps.",
      business: "Set one policy and stick to it today.",
      daily_life: "Say no to one small thing without explaining yourself."
    },
    easier_action: "Identify one boundary you wish you had set recently.",
    compass_lens: "west",
    compass_question: "What if you protected your time here?",
    fivec: "consequence",
    fivec_line: "Boundaries now prevent resentment later.",
    voice_line: "Boundary Setting - Know your limits early on. This is an important part of taking care of yourself."
  },
  "Attention to Detail": {
    id: "attention_to_detail",
    definition: "Catching small errors before they become big problems.",
    reps: {
      job_search: "Proofread one application before you hit send.",
      networking: "Spell someone's name correctly in your message.",
      interview: "Double-check the time and timezone before your call.",
      business: "Review one deliverable line by line.",
      daily_life: "Catch one mistake before it costs you time."
    },
    easier_action: "Review one thing you finished today for one small error.",
    compass_lens: "south",
    compass_question: "What process would catch this earlier?",
    fivec: "consequence",
    fivec_line: "Small errors add up over time.",
    voice_line: "Attention to Detail - The small things you miss are often the first things others notice."
  },
  "Generosity": {
    id: "generosity",
    definition: "Giving your time and knowledge freely within your limits.",
    reps: {
      job_search: "Share a useful resource with another job seeker.",
      networking: "Make an introduction without being asked.",
      interview: "Share a tip with someone else preparing for interviews.",
      business: "Give a piece of advice to someone earlier in their journey.",
      daily_life: "Help someone without expecting anything back."
    },
    easier_action: "Think of one way you could help someone today.",
    compass_lens: "north",
    compass_question: "What do they need that you can offer?",
    fivec: "care",
    fivec_line: "Generosity within limits is sustainable.",
    voice_line: "Generosity - Give what you can, but don't give so much that you have nothing left for yourself."
  }
};

// Context options
export const CONTEXTS = [
  { id: "job_search", label: "Job search" },
  { id: "networking", label: "Networking" },
  { id: "interview", label: "Interview" },
  { id: "business", label: "Building a business" },
  { id: "daily_life", label: "Daily life" }
];

// Compass directions
export const COMPASS_DIRECTIONS = {
  north: { name: "Needs", color: "compass-north" },
  east: { name: "Emotions", color: "compass-east" },
  south: { name: "Systems", color: "compass-south" },
  west: { name: "What If", color: "compass-west" }
};

// 5C categories
export const FIVE_CS = {
  care: { name: "Care", color: "fivec-care" },
  culture: { name: "Culture", color: "fivec-culture" },
  capacity: { name: "Capacity", color: "fivec-capacity" },
  consent: { name: "Consent", color: "fivec-consent" },
  consequence: { name: "Consequence", color: "fivec-consequence" }
};

// Helper to get skill details by name
export const getSkillDetails = (skillName) => SKILL_DETAILS[skillName] || null;
